import React, { useEffect, useMemo, useRef, useState } from "react";
import { createNewsletterApi, newsletterFields } from './newsletterApi';

export default function NewsletterPage({ contentApi, groupPublished }) {
  const api = useMemo(() => contentApi || createNewsletterApi(), [contentApi]);
  const versioned = !contentApi || contentApi.versioned === true;
  const isGroup = typeof groupPublished === 'boolean';
  const lock = useRef(false);
  const generation = useRef(0);
  const [newsletterTitle, setNewsletterTitle] = useState('');
  const [newsletterTagline, setNewsletterTagline] = useState('');
  const [baseline, setBaseline] = useState('');
  const [version, setVersion] = useState('');
  const [publicationPending, setPublicationPending] = useState(false);
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [ready, setReady] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const dirty = ready && JSON.stringify({ newsletterTitle, newsletterTagline }) !== baseline;
  const previewTitle = newsletterTitle || 'Your newsletter title';
  useEffect(() => {
    const current = ++generation.current;
    lock.current = false; setIsSaving(false);
    setIsLoading(true); setReady(false); setStatus('');
    api.get().then(data => {
      if (generation.current !== current) return;
      const value = newsletterFields(contentApi && data && Object.keys(data).length === 0 ? { newsletterTitle: '', newsletterTagline: '' } : data);
      setNewsletterTitle(value.newsletterTitle); setNewsletterTagline(value.newsletterTagline);
      setBaseline(JSON.stringify(value)); setVersion(data.version || '');
      setPublicationPending(data.publicationPending === true); setReady(true);
    }).catch(error => { if (generation.current === current) setStatus(error.message || 'Newsletter settings could not be loaded. Please retry.'); })
      .finally(() => { if (generation.current === current) setIsLoading(false); });
    return () => { generation.current = current + 1; };
  }, [api, attempt, contentApi]);
  useEffect(() => {
    const unload = event => { if (dirty || lock.current) { event.preventDefault(); event.returnValue = ''; } };
    const leave = event => { if (lock.current || (dirty && !window.confirm('Discard your unsaved Newsletter Signup Page changes?'))) event.preventDefault(); };
    window.addEventListener('beforeunload', unload);
    window.addEventListener('bookish:before-navigation', leave);
    return () => { window.removeEventListener('beforeunload', unload); window.removeEventListener('bookish:before-navigation', leave); };
  }, [dirty]);
  const reload = () => {
    if (lock.current || (dirty && !window.confirm('Reload the saved page and discard your unsaved newsletter edits?'))) return;
    setAttempt(value => value + 1);
  };
  const handleSave = async event => {
    event.preventDefault();
    if (!ready || lock.current) return;
    if (!newsletterTitle.trim()) { setStatus('Add a title before saving.'); return; }
    if (newsletterTitle.length > 200 || newsletterTagline.length > 2000) { setStatus('Use a title up to 200 characters and a tagline up to 2,000 characters.'); return; }
    lock.current = true; setIsSaving(true); setStatus('');
    const current = generation.current;
    const value = { newsletterTitle: newsletterTitle.trim(), newsletterTagline: newsletterTagline.trim() };
    try {
      const result = await api.save(versioned ? { ...value, version } : value);
      if (generation.current !== current) return;
      const saved = newsletterFields(versioned ? result : result?.item);
      setNewsletterTitle(saved.newsletterTitle); setNewsletterTagline(saved.newsletterTagline);
      setBaseline(JSON.stringify(saved)); setVersion(result?.version || ''); setPublicationPending(false);
      setStatus(isGroup ? (groupPublished ? 'Newsletter signup page saved. Your published group now shows these changes.' : 'Newsletter signup page saved. It will be visible when your group is published.') : 'Newsletter page published.');
    } catch (error) { if (generation.current === current) setStatus(error.message || 'Publication could not be confirmed. Reload the saved page to check before retrying.'); }
    finally { if (generation.current === current) { lock.current = false; setIsSaving(false); } }
  };

  return (
    <div className="bg-bookish-white/40 mt-10 border border-bookish-purple/20 p-4 sm:p-6 rounded-2xl shadow-sm">
      <div className="mx-auto max-w-4xl space-y-8">
        <div className={isGroup ? "group-start-heading" : "text-left"}><div>
          <h1 className="text-2xl sm:text-3xl font-serif font-semibold">
            Newsletter Signup Page
          </h1>
          <p className="mt-2 text-sm text-bookish-purple/80">
            Customize how readers discover your newsletter and preview what
            they&apos;ll see before you save. {isGroup ? (groupPublished ? 'Saving updates the signup page on your published group site.' : 'Saving prepares the signup page; your group stays unpublished.') : 'Saving publishes this signup page.'} It does not send a newsletter email.
          </p>
        </div></div>

        <form onSubmit={handleSave} aria-busy={isLoading || isSaving} className="rounded-2xl border border-bookish-purple/15 bg-bookish-cream/80 p-5 sm:p-6 shadow-sm space-y-5">
          <div>
            <label htmlFor="newsletter-title" className="block text-sm font-medium mb-2">
              Your Call to Action
            </label>
            <textarea
              rows={2}
              id="newsletter-title"
              disabled={!ready || isSaving}
              maxLength={200}
              value={newsletterTitle}
              onChange={(e) => { setNewsletterTitle(e.target.value); setStatus(''); }}
              required
              aria-describedby="newsletter-title-help"
              placeholder="A new chapter in your inbox"
              className="w-full min-h-[44px] px-4 py-3 rounded-lg border border-bookish-purple/20 focus:ring-2 focus:ring-bookish-purpleAccent outline-none"
            />
            <p id="newsletter-title-help" className="text-xs mt-2">{newsletterTitle.length}/200 characters</p>
          </div>

          <div>
            <label htmlFor="newsletter-tagline" className="block text-sm font-medium mb-2">
              Add a tagline (optional)
            </label>
            <textarea
              rows={4}
              id="newsletter-tagline"
              disabled={!ready || isSaving}
              maxLength={2000}
              value={newsletterTagline}
              onChange={(e) => { setNewsletterTagline(e.target.value); setStatus(''); }}
              aria-describedby="newsletter-tagline-help"
              placeholder="Fresh stories, updates, and behind-the-scenes notes delivered to your inbox."
              className="w-full min-h-[44px] px-4 py-3 rounded-lg border border-bookish-purple/20 focus:ring-2 focus:ring-bookish-purpleAccent outline-none"
            />
          </div>

          <p id="newsletter-tagline-help" className="text-xs">{newsletterTagline.length}/2,000 characters. Leave blank to show no tagline.</p>
          <div className="flex flex-wrap gap-3 justify-center pt-2">
            <button
              type="submit"
              disabled={!ready || isLoading || isSaving}
              className="bg-bookish-purple text-white max-w-full px-4 sm:px-8 py-3 rounded-xl hover:bg-bookish-purpleAccent transition disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isLoading
                ? "Loading Newsletter Page..."
                : isSaving ? "Saving..." : "Save Newsletter Page"}
            </button>
            {ready && <button type="button" disabled={isSaving} onClick={reload} className="underline min-h-[44px] px-3">Reload saved page</button>}
          </div>

          <p role="status" className="text-sm text-center">{status || (dirty ? 'You have unsaved changes.' : '')}</p>
          {ready && publicationPending && <p role="alert">Recovered settings have not been published. Review them and save to update the reader page.</p>}
          {!isLoading && !ready && <button type="button" className="underline min-h-[44px]" onClick={reload}>Retry loading newsletter settings</button>}
        </form>

        {/* Non-interactive preview: no subscription requests are sent. */}
        <div className="rounded-2xl border border-bookish-purple/15 bg-bookish-cream/80 p-5 sm:p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-2">Reader Preview</h2>
          <p className="text-sm mb-4">Preview only. This form cannot subscribe anyone.</p>

          <div className="max-w-2xl mx-auto mt-6 sm:mt-10 px-0 sm:px-4 text-bookish-purple">
            <div className="text-center mb-8">
              <p className="text-2xl sm:text-4xl font-serif font-semibold mb-2 break-words whitespace-pre-wrap">
                {previewTitle}
              </p>
              {newsletterTagline && <p className="text-bookish-purple/70 break-words whitespace-pre-wrap">{newsletterTagline}</p>}
            </div>

            <div className="rounded-2xl shadow-md bg-bookish-white border border-bookish-purple/10 p-4 sm:p-8">
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-left">
                  <div>
                    <label htmlFor="newsletter-preview-first" className="block text-sm font-medium mb-1">
                      First Name
                    </label>
                    <input
                      disabled
                      id="newsletter-preview-first"
                      value="Your first name"
                      readOnly
                      className="w-full px-4 py-2 rounded-lg border border-bookish-purple/20 bg-white text-bookish-purple/50"
                    />
                  </div>

                  <div>
                    <label htmlFor="newsletter-preview-last" className="block text-sm font-medium mb-1">
                      Last Name
                    </label>
                    <input
                      disabled
                      id="newsletter-preview-last"
                      value="Your last name"
                      readOnly
                      className="w-full px-4 py-2 rounded-lg border border-bookish-purple/20 bg-white text-bookish-purple/50"
                    />
                  </div>
                </div>

                <div className="text-left">
                  <label htmlFor="newsletter-preview-email" className="block text-sm font-medium mb-1">
                    Email Address
                  </label>
                  <input
                    disabled
                    id="newsletter-preview-email"
                    value="reader@example.com"
                    readOnly
                    className="w-full px-4 py-2 rounded-lg border border-bookish-purple/20 bg-white text-bookish-purple/50"
                  />
                </div>

                <button
                  disabled
                  className="w-full bg-bookish-purple text-white/90 py-3 rounded-2xl text-lg font-semibold shadow-lg"
                >
                  Sign Up
                </button>

                <p className="text-xs text-bookish-purple/60 text-center leading-relaxed">
                  By subscribing, you agree to receive newsletter emails. You
                  can unsubscribe at any time via the link in each email.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
